import React from 'react';
import { Calendar, ExternalLink, CheckSquare, Info, Flag } from 'lucide-react';

/**
 * MOSAIC-NY1 · Pillar 1 · New York 2027 Readiness
 *
 * Visibly separates:
 *   - "Official milestone (NYSED)" factual timeline entries
 *   - "MOSAIC recommendation" MOSAIC-authored suggestions
 *
 * All official entries carry: source organization, URL, last-verified metadata.
 * UNCONFIRMED dates are rendered in the isolated preview with an UNCONFIRMED flag
 * and are NOT production-approved until operator review.
 */

// Factual NYSED / WIDA transition milestones.
// Sources: nysed.gov/state-assessment/wida-consortium, wida.wisc.edu/memberships/consortium/ny
// Last-verified metadata is per-entry so individual dates can be refreshed without
// invalidating the whole set.
export const NY_OFFICIAL_MILESTONES = [
  {
    id: 'milestone-transition-announcement',
    date: '2024–2025',
    event: 'NYSED announced NY State transition from NYSESLAT to WIDA ACCESS',
    source_org: 'NYSED',
    source_url: 'https://www.nysed.gov/state-assessment/wida-consortium',
    last_verified: '2026-02-06',
    status: 'VERIFIED',
  },
  {
    id: 'milestone-final-nyseslat',
    date: 'May 22, 2026',
    event: 'Final NYSESLAT administration concludes',
    detail: 'New York\'s final NYSESLAT administration took place in Spring 2026. Speaking: April 6–May 22, 2026; Listening/Reading/Writing: May 4–May 22, 2026.',
    source_org: 'NYSED',
    source_url: 'https://www.nysed.gov/state-assessment/wida-consortium',
    last_verified: '2026-10-06',
    status: 'VERIFIED',
  },
  {
    id: 'milestone-transition-year',
    date: '2026–2027',
    event: 'Transition year — NYSED and districts prepare for WIDA ACCESS',
    source_org: 'NYSED',
    source_url: 'https://www.nysed.gov/state-assessment/wida-consortium',
    last_verified: '2026-02-06',
    status: 'VERIFIED',
  },
  {
    id: 'milestone-first-wida-access',
    date: 'Spring 2027',
    event: 'First New York administration of WIDA ACCESS',
    source_org: 'NYSED / WIDA',
    source_url: 'https://wida.wisc.edu/memberships/consortium/ny',
    last_verified: '2026-02-06',
    status: 'VERIFIED',
  },
  {
    id: 'milestone-wida-screener',
    date: 'Fall 2027',
    event: 'WIDA Screener replaces NYSITELL for initial ELL identification in NY',
    source_org: 'NYSED',
    source_url: 'https://www.nysed.gov/state-assessment/wida-consortium',
    last_verified: '2026-02-06',
    status: 'VERIFIED',
  },
];

// MOSAIC-authored educator readiness checklist. Original wording — does not
// paraphrase NYSED or WIDA guidance.
export const MOSAIC_EDUCATOR_CHECKLIST = [
  'Confirm your current ELL roster and expected accommodations.',
  'Review each student\'s most recent MOSAIC practice session.',
  'Verify listening audio playback on the devices students will use.',
  'Print backup paper booklets for any student who may test offline.',
  'Confirm the USB/offline backup opens and runs on at least one device.',
  'Note your district\'s published testing window dates where you can see them.',
  'Share a short family note explaining MOSAIC is practice preparation.',
];

// Official NYSED / WIDA source links. Link, do not restate guidance text.
export const NY_OFFICIAL_SOURCES = [
  {
    label: 'NYSED — WIDA Consortium Transition',
    url: 'https://www.nysed.gov/state-assessment/wida-consortium',
    org: 'NYSED',
    last_verified: '2026-02-06',
  },
  {
    label: 'NYSED — Office of Bilingual Education',
    url: 'https://www.nysed.gov/bilingual-ed',
    org: 'NYSED',
    last_verified: '2026-02-06',
  },
  {
    label: 'WIDA Consortium — New York Member Page',
    url: 'https://wida.wisc.edu/memberships/consortium/ny',
    org: 'WIDA',
    last_verified: '2026-02-06',
  },
  {
    label: 'WIDA ACCESS for ELLs — Official',
    url: 'https://wida.wisc.edu/assess/access',
    org: 'WIDA',
    last_verified: '2026-02-06',
  },
];

const StatusBadge = ({ status }) => {
  if (status === 'UNCONFIRMED') {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5"
        title="UNCONFIRMED — not production-approved until operator review"
      >
        <Info size={10} /> UNCONFIRMED
      </span>
    );
  }
  return null;
};

const NYReadinessCard = ({ compact = false }) => {
  return (
    <div
      className="border-2 border-[#002FA7] bg-white"
      data-testid="ny-readiness-card"
    >
      {/* Header */}
      <div className="bg-[#002FA7] text-white px-5 py-3 flex items-center gap-2">
        <Flag size={18} />
        <div>
          <div className="font-bold text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
            New York 2027 Readiness
          </div>
          <div className="text-[11px] text-blue-100">
            Official milestones and MOSAIC recommendations — kept visibly distinct
          </div>
        </div>
      </div>

      {/* Official milestones */}
      <div className="p-5 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={16} className="text-[#002FA7]" />
          <h4 className="font-bold text-sm uppercase tracking-wider text-[#002FA7]">
            Official milestone (NYSED)
          </h4>
        </div>
        <ul className="space-y-2.5" data-testid="ny-official-milestones">
          {NY_OFFICIAL_MILESTONES.map((m) => (
            <li
              key={m.id}
              className="flex gap-3 text-sm"
              data-testid={`ny-milestone-${m.id}`}
            >
              <div className="w-24 flex-shrink-0 font-semibold text-gray-700">
                {m.date}
              </div>
              <div className="flex-1">
                <div className="flex items-start gap-2 flex-wrap">
                  <span className="text-gray-800">{m.event}</span>
                  <StatusBadge status={m.status} />
                </div>
                {m.detail && (
                  <div className="text-[12px] text-gray-600 mt-0.5">
                    {m.detail}
                  </div>
                )}
                <div className="text-[11px] text-gray-500 mt-0.5">
                  Source: {m.source_org} ·{' '}
                  <a
                    href={m.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[#002FA7]"
                  >
                    link
                  </a>{' '}
                  · verified {m.last_verified}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* MOSAIC recommendations */}
      {!compact && (
        <div className="p-5 border-b border-gray-200 bg-green-50">
          <div className="flex items-center gap-2 mb-3">
            <CheckSquare size={16} className="text-green-700" />
            <h4 className="font-bold text-sm uppercase tracking-wider text-green-800">
              MOSAIC recommendation
            </h4>
          </div>
          <p className="text-xs text-green-900/80 mb-3 italic">
            MOSAIC suggestions. These are not NYSED or WIDA requirements.
          </p>
          <ul className="space-y-1.5" data-testid="ny-mosaic-checklist">
            {MOSAIC_EDUCATOR_CHECKLIST.map((item, idx) => (
              <li
                key={idx}
                className="flex gap-2 text-sm text-gray-800"
                data-testid={`ny-checklist-${idx}`}
              >
                <span className="text-green-700 font-bold">·</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Official sources */}
      {!compact && (
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <ExternalLink size={16} className="text-gray-600" />
            <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700">
              Official New York transition sources
            </h4>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2" data-testid="ny-official-sources">
            {NY_OFFICIAL_SOURCES.map((s) => (
              <li key={s.url} className="text-sm">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-start gap-1.5 text-[#002FA7] hover:underline"
                >
                  <ExternalLink size={12} className="mt-1 flex-shrink-0" />
                  <span>
                    <span className="font-medium">{s.label}</span>
                    <span className="block text-[11px] text-gray-500">
                      {s.org} · verified {s.last_verified}
                    </span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NYReadinessCard;
